import NiceModal from "@ebay/nice-modal-react"
import cx from "classnames"
import {
    Archive,
    ArchiveRestore,
    ArrowDown,
    ArrowUp,
    ArrowUpRight,
    ChevronDown,
    ChevronRight,
    Copy,
    Eye,
    EyeOff,
    FolderOpen,
    Loader2,
    Monitor,
    MoreHorizontal,
    Pause,
    Play,
    Plus,
    Settings,
    Share2,
    Trash2,
} from "lucide-react"
import { observer } from "mobx-react"
import { type DragEvent as ReactDragEvent, type ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { type RemoteFleetProject, remoteFleetProjects, remoteFleetTasks, shortComputerName, sourceLabel } from "../../computers/fleet"
import { openPathInFileManager } from "../../electronAPIs/shell"
import { getFileManagerName } from "../../nestAPIs/platform"
import { getPlatformAdapter } from "../../platform/adapter"
import { type ProjectColor, projectColorAssignments, projectColorValue } from "../../projectAppearance"
import { useCodeNavigate } from "../../routing"
import { useCodeStore } from "../../store/context"
import type { Repo } from "../../types"
import { copyTextToClipboard } from "../../utils/clipboard"
import { ContextMenu, Menu, type MenuItem, type MenuSection } from "../ui"
import { useOptionalComputers } from "../../computers/context"
import { PersonGlyph } from "../computers/PersonGlyph"
import { DiscoveryButton } from "../discovery/DiscoveryButton"
import { ShareSheet } from "../sharing/ShareSheet"
import { loadFleetSidebarState, saveFleetSidebarState } from "./fleetSidebarState"
import { GridDisclosure } from "./GridDisclosure"
import { ProjectIcon } from "./ProjectIcon"
import { queueModeManager } from "./queueModeManager"
import { ScrollArea } from "../ui/ScrollArea"
import { codeSidebarManager } from "./sidebarManager"
import type { SidebarRemoteQueue } from "./TaskList"
import { hiddenWorkspaceIdsForOnly, loadWorkspaceSidebarState, moveWorkspace, orderWorkspaces, saveWorkspaceSidebarState } from "./workspaceSidebarState"

/**
 * One row of the project list. Projects on the active computer come from
 * the store and carry a path; projects elsewhere come from that computer's
 * bounded overview and carry where they live instead.
 */
export interface SidebarProject {
    id: string
    name: string
    archived: boolean
    path?: string
    color?: ProjectColor
    glyph?: string
    projectChars?: string
    projectEmoji?: string
    remote?: RemoteFleetProject
}

function repoToProject(repo: Repo): SidebarProject {
    return {
        id: repo.id,
        name: repo.name,
        archived: !!repo.archived,
        path: repo.path,
        ...(repo.color ? { color: repo.color } : {}),
        ...(repo.glyph ? { glyph: repo.glyph } : {}),
        projectChars: repo.projectChars,
        projectEmoji: repo.projectEmoji,
    }
}

function remoteToProject(remote: RemoteFleetProject): SidebarProject {
    return {
        id: remote.id,
        name: remote.name,
        archived: false,
        ...(remote.color ? { color: remote.color } : {}),
        ...(remote.glyph ? { glyph: remote.glyph } : {}),
        projectChars: remote.projectChars,
        projectEmoji: remote.projectEmoji,
        remote,
    }
}

function refreshedLabel(refreshedAt: string): string {
    const elapsed = Date.now() - new Date(refreshedAt).getTime()
    if (!Number.isFinite(elapsed) || elapsed < 60_000) return "just now"
    const minutes = Math.floor(elapsed / 60_000)
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    return hours < 24 ? `${hours}h ago` : `${Math.floor(hours / 24)}d ago`
}

/** The quiet mark at a row's edge that says which computer a project is on. */
const SourceTag = ({ remote, compact = false }: { remote: RemoteFleetProject; compact?: boolean }) => {
    const { source } = remote
    const title = `${sourceLabel(source)} · ${remote.status === "offline" ? "unreachable" : remote.status === "unknown" ? "last seen" : "updated"} ${refreshedLabel(remote.refreshedAt)}`
    return (
        <span className="inline-flex flex-none items-center gap-1" data-project-source={source.kind} title={compact ? undefined : title}>
            {remote.status === "offline" ? <span className="h-1.5 w-1.5 flex-none bg-error" aria-label="Unreachable" /> : null}
            {source.kind === "shared" ? (
                <PersonGlyph name={source.personName} glyph={source.glyph} color={source.color} size="sm" />
            ) : source.kind === "own" ? (
                <span
                    className={cx("inline-flex items-center gap-1 text-[10px] text-muted", compact ? "justify-center" : "max-w-[6.5rem]")}
                    aria-label={compact ? `On ${shortComputerName(source.computerName)}` : undefined}
                >
                    <Monitor className="h-3 w-3 flex-none" />
                    {!compact ? <span className="truncate">{shortComputerName(source.computerName)}</span> : null}
                </span>
            ) : null}
        </span>
    )
}

interface RepoMenuActions {
    project: SidebarProject
    isHidden: boolean
    canMoveUp: boolean
    canMoveDown: boolean
    onMoveUp: () => void
    onMoveDown: () => void
    onSettings: () => void
    onDelete: () => void
    onToggleArchive: () => void
    onToggleHidden: () => void
    onOpen?: () => void
    onShare?: () => void
}

function projectMenuSections({
    project,
    isHidden,
    canMoveUp,
    canMoveDown,
    onMoveUp,
    onMoveDown,
    onSettings,
    onDelete,
    onToggleArchive,
    onToggleHidden,
    onOpen,
    onShare,
}: RepoMenuActions): MenuSection[] {
    const fileManagerName = getFileManagerName()
    const hideItem: MenuItem = {
        id: "hide",
        label: (
            <div className="flex items-center gap-2">
                {isHidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                <span>{isHidden ? "Unhide" : "Hide"}</span>
            </div>
        ),
        onSelect: onToggleHidden,
    }
    const remoteItems: MenuItem[] = project.remote
        ? [
              ...(onOpen
                  ? [
                        {
                            id: "open-computer",
                            label: (
                                <div className="flex items-center gap-2">
                                    <ArrowUpRight className="w-4 h-4" />
                                    <span>
                                        Open on{" "}
                                        {shortComputerName(project.remote.source.kind === "local" ? "this computer" : project.remote.source.computerName)}
                                    </span>
                                </div>
                            ),
                            onSelect: onOpen,
                        } satisfies MenuItem,
                    ]
                  : []),
              hideItem,
          ]
        : []

    const menuItems: MenuItem[] = [
        ...(getPlatformAdapter().capabilities.openHostPath === "available"
            ? [
                  {
                      id: "open-in-file-manager",
                      label: (
                          <div className="flex items-center gap-2">
                              <FolderOpen className="w-4 h-4" />
                              <span>Open in {fileManagerName}</span>
                          </div>
                      ),
                      onSelect: () => {
                          if (project.path) openPathInFileManager(project.path)
                      },
                  } satisfies MenuItem,
              ]
            : []),
        {
            id: "copy-path",
            label: (
                <div className="flex items-center gap-2">
                    <Copy className="w-4 h-4" />
                    <span>Copy path</span>
                </div>
            ),
            onSelect: async () => {
                try {
                    await copyTextToClipboard(project.path ?? project.name)
                } catch (err) {
                    console.error("[RepoList] Failed to copy workspace path:", err)
                }
            },
        },
        ...(onShare
            ? [
                  {
                      id: "share",
                      label: (
                          <div className="flex items-center gap-2">
                              <Share2 className="w-4 h-4" />
                              <span>Share…</span>
                          </div>
                      ),
                      onSelect: onShare,
                  } satisfies MenuItem,
              ]
            : []),
        {
            id: "settings",
            label: (
                <div className="flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    <span>Settings</span>
                </div>
            ),
            onSelect: onSettings,
        },
        {
            id: "move-up",
            label: (
                <div className="flex items-center gap-2">
                    <ArrowUp className="w-4 h-4" />
                    <span>Move up</span>
                </div>
            ),
            disabled: !canMoveUp,
            onSelect: onMoveUp,
        },
        {
            id: "move-down",
            label: (
                <div className="flex items-center gap-2">
                    <ArrowDown className="w-4 h-4" />
                    <span>Move down</span>
                </div>
            ),
            disabled: !canMoveDown,
            onSelect: onMoveDown,
        },
        hideItem,
        {
            id: "archive",
            label: (
                <div className="flex items-center gap-2">
                    {project.archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                    <span>{project.archived ? "Unarchive" : "Archive"}</span>
                </div>
            ),
            onSelect: onToggleArchive,
        },
        {
            id: "delete",
            label: (
                <div className="flex items-center gap-2">
                    <Trash2 className="w-4 h-4" />
                    <span>Delete</span>
                </div>
            ),
            onSelect: onDelete,
        },
    ]

    if (project.remote) return [{ items: remoteItems }]
    return [
        { items: menuItems.filter((item) => item.id === "open-in-file-manager" || item.id === "copy-path") },
        { items: menuItems.filter((item) => item.id === "share" || item.id === "settings") },
        { items: menuItems.filter((item) => item.id === "move-up" || item.id === "move-down") },
        { items: menuItems.filter((item) => item.id === "hide" || item.id === "archive" || item.id === "delete") },
    ]
}

const RepoMenuButton = ({ projectName, sections }: { projectName: string; sections: MenuSection[] }) => (
    <Menu
        trigger={<MoreHorizontal className="h-3.5 w-3.5" />}
        triggerAriaLabel={`Project options for ${projectName}`}
        triggerTitle={`Project options for ${projectName}`}
        sections={sections}
        side="right"
        align="start"
        sideOffset={8}
        className={{ trigger: "!h-7 !w-7 !p-0 !border-0 !bg-transparent text-muted hover:!bg-base-300 hover:text-base-content" }}
    />
)

const RepoItemRow = ({
    project,
    projectColor,
    shown,
    runningCount,
    isArchived,
    isHidden,
    onToggleHidden,
    onOpen,
    isDragging,
    dropEdge,
    canMoveUp,
    canMoveDown,
    onSelect,
    onShowOnly,
    showOnlyDisabled,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
    onNewTask,
    onMoveUp,
    onMoveDown,
    onSettings,
    onDelete,
    onToggleArchive,
    onShare,
    onPreviewChange,
}: {
    project: SidebarProject
    projectColor: string
    shown: boolean
    runningCount: number
    isArchived: boolean
    isHidden: boolean
    onToggleHidden: () => void
    onOpen?: () => void
    isDragging: boolean
    dropEdge: "before" | "after" | null
    canMoveUp: boolean
    canMoveDown: boolean
    onSelect: () => void
    onShowOnly?: () => void
    showOnlyDisabled: boolean
    onDragStart: (event: ReactDragEvent<HTMLElement>) => void
    onDragOver: (event: ReactDragEvent<HTMLElement>) => void
    onDrop: (event: ReactDragEvent<HTMLElement>) => void
    onDragEnd: () => void
    onNewTask: () => void
    onMoveUp: () => void
    onMoveDown: () => void
    onSettings: () => void
    onDelete: () => void
    onToggleArchive: () => void
    onShare?: () => void
    onPreviewChange: (projectName: string | null) => void
}) => {
    const isTouchDevice = window.matchMedia("(hover: none) and (pointer: coarse)").matches
    const isExcluded = !shown && !isArchived && !isHidden
    const canDrag = !isTouchDevice && !project.remote && !isHidden
    const rowAction = isArchived
        ? `Unarchive ${project.name}`
        : isHidden
          ? `Unhide ${project.name}`
          : shown
            ? `Hide ${project.name} tasks`
            : `Show ${project.name} tasks`
    const menuSections = projectMenuSections({
        project,
        isHidden,
        canMoveUp,
        canMoveDown,
        onMoveUp,
        onMoveDown,
        onSettings,
        onDelete,
        onToggleArchive,
        onToggleHidden,
        onOpen,
        onShare,
    })
    const pill = (
        <div
            role="button"
            tabIndex={0}
            draggable={canDrag}
            className={cx(
                "group/project btn relative inline-flex h-8 min-w-8 items-center justify-center gap-1.5 px-1.5 font-normal transition-[background-color,opacity] duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary/50 max-[900px]:h-11 max-[900px]:min-w-12",
                isTouchDevice ? "cursor-pointer" : "cursor-grab active:cursor-grabbing",
                isExcluded ? "hover:bg-base-300/70 focus-visible:bg-base-300/70" : "hover:brightness-110",
                (isArchived || isHidden) && "opacity-55",
                isDragging && "opacity-35"
            )}
            style={{
                backgroundColor: shown ? `color-mix(in srgb, ${projectColor} 8%, transparent)` : undefined,
                color: projectColor,
            }}
            data-project-id={project.id}
            data-project-node={project.remote?.nodeId}
            data-project-visible={shown ? "true" : "false"}
            aria-label={rowAction}
            aria-pressed={isArchived || isHidden ? undefined : shown}
            onClick={onSelect}
            onMouseEnter={() => onPreviewChange(project.name)}
            onMouseLeave={(event) => {
                if (!event.currentTarget.contains(document.activeElement)) onPreviewChange(null)
            }}
            onFocus={() => onPreviewChange(project.name)}
            onBlur={(event) => {
                if (!(event.relatedTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget)) onPreviewChange(null)
            }}
            onDoubleClick={(event) => {
                if (!onShowOnly || showOnlyDisabled) return
                if (event.target instanceof Element && event.target.closest("button")) return
                event.preventDefault()
                event.stopPropagation()
                onShowOnly()
            }}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onDragEnd={onDragEnd}
            onKeyDown={(event) => {
                if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
                    event.preventDefault()
                    onSelect()
                }
            }}
        >
            {dropEdge ? (
                <span
                    aria-hidden="true"
                    className={cx("pointer-events-none absolute inset-y-0 z-10 w-0.5 bg-primary", dropEdge === "before" ? "-left-1" : "-right-1")}
                />
            ) : null}
            <ProjectIcon
                projectId={project.id}
                projectName={project.name}
                color={projectColor}
                glyph={project.glyph}
                projectChars={project.projectChars}
                projectEmoji={project.projectEmoji}
                nativeTitle={false}
                variant="glyph"
                className={cx(
                    "transition-[opacity,filter] duration-150",
                    isExcluded &&
                        "opacity-60 saturate-75 group-hover/project:opacity-100 group-hover/project:saturate-100 group-focus-visible/project:opacity-100 group-focus-visible/project:saturate-100"
                )}
            />
            {shown ? <span aria-hidden="true" className="absolute inset-x-2 bottom-0 h-0.5 bg-current opacity-70" /> : null}
            {project.remote ? <SourceTag remote={project.remote} compact /> : null}
            {runningCount > 0 ? (
                <span
                    className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center bg-base-200 text-success"
                    aria-label={`${runningCount} running`}
                >
                    <Loader2 className="h-2.5 w-2.5 animate-spin" />
                    <span className="sr-only">{runningCount}</span>
                </span>
            ) : null}
            {!isArchived && !project.remote ? (
                <button
                    type="button"
                    className={cx(
                        "btn absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center border border-border bg-base-200 text-muted shadow-sm transition-[color,opacity] hover:text-base-content focus:text-base-content max-[900px]:h-5 max-[900px]:w-5",
                        !isTouchDevice && "opacity-0 group-hover/project:opacity-100 focus:opacity-100"
                    )}
                    onClick={(event) => {
                        event.stopPropagation()
                        onNewTask()
                    }}
                    onDragStart={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                    }}
                    title={`New task in ${project.name}`}
                    aria-label={`New task in ${project.name}`}
                >
                    <Plus className="h-2.5 w-2.5" />
                </button>
            ) : null}
            {isTouchDevice ? (
                <span onClick={(event) => event.stopPropagation()}>
                    <RepoMenuButton projectName={project.name} sections={menuSections} />
                </span>
            ) : null}
            {isHidden ? <EyeOff className="h-3 w-3 flex-none" /> : null}
        </div>
    )

    return <ContextMenu trigger={pill} sections={menuSections} className={{ trigger: "inline-flex" }} />
}

interface ReposSidebarContentProps {
    renderMergedContent: (repos: Repo[], workspaceColorById: ReadonlyMap<string, string>, remoteQueues: SidebarRemoteQueue[]) => ReactNode
    storageScope: string
    workspaceId: string | undefined
}

type WorkspaceDropTarget = {
    edge: "before" | "after"
    repoId: string
}

export const ReposSidebarContent = observer(({ renderMergedContent, storageScope, workspaceId }: ReposSidebarContentProps) => {
    const codeStore = useCodeStore()
    const computers = useOptionalComputers()
    const navigate = useCodeNavigate()
    const shareProject =
        codeStore.isGuest || !computers
            ? undefined
            : (repoId: string) => {
                  void NiceModal.show(ShareSheet, {
                      nodeId: computers.scope,
                      projects: codeStore.repos.repos
                          .filter((repo) => !repo.archived)
                          .map((repo) => ({
                              id: repo.id,
                              name: repo.name,
                              ...(repo.color ? { color: projectColorValue(repo.color) } : {}),
                              ...(repo.glyph ? { glyph: repo.glyph } : {}),
                              projectChars: repo.projectChars,
                              projectEmoji: repo.projectEmoji,
                          })),
                      initialProjectId: repoId,
                  })
              }
    const [initialSidebarState] = useState(() => loadWorkspaceSidebarState(storageScope))
    const [initialFleetState] = useState(() => loadFleetSidebarState())
    const [hiddenProjectIds, setHiddenProjectIds] = useState<Set<string>>(() => new Set(initialFleetState.hiddenProjectIds))
    const [showHidden, setShowHidden] = useState(initialFleetState.hiddenVisible)
    // Which of a remote computer's projects are excluded from the blend lives
    // in that computer's own sidebar record, exactly like the active one.
    const [remoteExcludedByNode, setRemoteExcludedByNode] = useState<ReadonlyMap<string, ReadonlySet<string>>>(() => new Map())
    const [showArchived, setShowArchived] = useState(initialSidebarState?.archivedVisible ?? false)
    const [workspaceOrder, setWorkspaceOrder] = useState<string[]>(initialSidebarState?.workspaceOrder ?? [])
    const [hiddenRepoIds, setHiddenRepoIds] = useState<Set<string>>(() => new Set(initialSidebarState?.hiddenWorkspaceIds ?? []))
    const [previewProjectName, setPreviewProjectName] = useState<string | null>(null)
    const [draggedRepoId, setDraggedRepoId] = useState<string | null>(null)
    const [workspaceDropTarget, setWorkspaceDropTarget] = useState<WorkspaceDropTarget | null>(null)
    const draggedRepoIdRef = useRef<string | null>(null)
    const suppressSelectAfterDragRef = useRef(false)

    const allRepos = codeStore.repos.repos
    const orderedRepos = orderWorkspaces(allRepos, workspaceOrder)
    const activeRepos = orderedRepos.filter((r) => !r.archived && !hiddenProjectIds.has(r.id))
    const hiddenRepos = orderedRepos.filter((r) => !r.archived && hiddenProjectIds.has(r.id))
    const archivedRepos = orderedRepos.filter((r) => r.archived)
    const remoteProjects = useMemo(
        () =>
            computers
                ? remoteFleetProjects({
                      computers: computers.state.computers,
                      people: computers.people,
                      overviews: computers.overviews,
                      activeNodeId: storageScope,
                  })
                : [],
        [computers, storageScope]
    )
    const visibleRemote = remoteProjects.filter((project) => !hiddenProjectIds.has(project.id))
    const hiddenRemote = remoteProjects.filter((project) => hiddenProjectIds.has(project.id))
    const excludedFor = (nodeId: string): ReadonlySet<string> =>
        remoteExcludedByNode.get(nodeId) ?? new Set(loadWorkspaceSidebarState(nodeId)?.hiddenWorkspaceIds ?? [])
    const shownRemote = visibleRemote.filter((project) => !excludedFor(project.nodeId).has(project.id))
    const activeWorkspaceColorById = projectColorAssignments([...activeRepos, ...visibleRemote])
    const archivedWorkspaceColorById = projectColorAssignments(archivedRepos)
    const shownTaskRepos = activeRepos.filter((repo) => !hiddenRepoIds.has(repo.id))
    const routedProjectName = allRepos.find((repo) => repo.id === workspaceId)?.name ?? remoteProjects.find((project) => project.id === workspaceId)?.name
    const projectNamePreview = previewProjectName ?? routedProjectName
    const remoteQueues: SidebarRemoteQueue[] = shownRemote.map((project) => ({
        workspace: {
            id: project.id,
            name: project.name,
            color: activeWorkspaceColorById.get(project.id),
            glyph: project.glyph,
            projectChars: project.projectChars,
            projectEmoji: project.projectEmoji,
        },
        nodeId: project.nodeId,
        source: project.source,
        tasks: remoteFleetTasks(computers?.overviews[project.nodeId], project.id),
    }))
    const sidebarTasksByRepo = new Map(allRepos.map((repo) => [repo.id, codeStore.tasks.getSidebarTasksForRepo(repo.id)]))
    const runningTaskCount = codeStore.runtimes.runningTaskIds.size

    const getRunningCount = (repoId: string): number => {
        const runningTasks = (sidebarTasksByRepo.get(repoId) ?? []).filter((task) => codeStore.isTaskRunning(task.id)).length
        const pendingCreations = codeStore.creation.getCreationsForRepo(repoId).filter((creation) => !creation.error).length
        return runningTasks + pendingCreations
    }

    useEffect(() => {
        saveWorkspaceSidebarState(storageScope, {
            archivedVisible: showArchived,
            hiddenWorkspaceIds: [...hiddenRepoIds],
            workspaceOrder,
        })
    }, [hiddenRepoIds, showArchived, storageScope, workspaceOrder])

    useEffect(() => {
        saveFleetSidebarState({ hiddenProjectIds: [...hiddenProjectIds], hiddenVisible: showHidden })
    }, [hiddenProjectIds, showHidden])

    useLayoutEffect(() => {
        queueModeManager.setStorageScope(storageScope)
        queueModeManager.setRunningTaskCount(runningTaskCount)
    }, [runningTaskCount, storageScope])

    const reorderWorkspace = (sourceRepoId: string, targetRepoId: string, edge: "before" | "after") => {
        const sourceRepo = allRepos.find((repo) => repo.id === sourceRepoId)
        const targetRepo = allRepos.find((repo) => repo.id === targetRepoId)
        if (!sourceRepo || !targetRepo || !!sourceRepo.archived !== !!targetRepo.archived) return

        setWorkspaceOrder(
            moveWorkspace(
                orderedRepos.map((repo) => repo.id),
                sourceRepoId,
                targetRepoId,
                edge
            )
        )
    }

    const moveWorkspaceByOffset = (repo: Repo, offset: -1 | 1) => {
        const peers = repo.archived ? archivedRepos : activeRepos
        const currentIndex = peers.findIndex((peer) => peer.id === repo.id)
        const target = peers[currentIndex + offset]
        if (!target) return
        reorderWorkspace(repo.id, target.id, offset < 0 ? "before" : "after")
    }

    const resetWorkspaceDrag = () => {
        draggedRepoIdRef.current = null
        setDraggedRepoId(null)
        setWorkspaceDropTarget(null)
        setTimeout(() => {
            suppressSelectAfterDragRef.current = false
        }, 0)
    }

    const handleWorkspaceDragStart = (event: ReactDragEvent<HTMLElement>, repoId: string) => {
        suppressSelectAfterDragRef.current = true
        draggedRepoIdRef.current = repoId
        setDraggedRepoId(repoId)
        setWorkspaceDropTarget(null)
        event.dataTransfer.effectAllowed = "move"
        event.dataTransfer.setData("text/plain", repoId)
    }

    const handleWorkspaceDragOver = (event: ReactDragEvent<HTMLElement>, targetRepo: Repo) => {
        const sourceRepoId = draggedRepoIdRef.current
        const sourceRepo = allRepos.find((repo) => repo.id === sourceRepoId)
        if (!sourceRepoId || sourceRepoId === targetRepo.id || !sourceRepo || !!sourceRepo.archived !== !!targetRepo.archived) {
            setWorkspaceDropTarget(null)
            return
        }

        event.preventDefault()
        event.dataTransfer.dropEffect = "move"
        const bounds = event.currentTarget.getBoundingClientRect()
        const edge = event.clientX < bounds.left + bounds.width / 2 ? "before" : "after"
        setWorkspaceDropTarget((current) => (current?.repoId === targetRepo.id && current.edge === edge ? current : { edge, repoId: targetRepo.id }))
    }

    const handleWorkspaceDrop = (event: ReactDragEvent<HTMLElement>, targetRepoId: string) => {
        event.preventDefault()
        const sourceRepoId = draggedRepoIdRef.current
        if (sourceRepoId && workspaceDropTarget?.repoId === targetRepoId) {
            reorderWorkspace(sourceRepoId, targetRepoId, workspaceDropTarget.edge)
        }
        resetWorkspaceDrag()
    }

    const handleAddRepo = () => {
        navigate.go("CodeWorkspaceCreate")
        codeSidebarManager.closeSidebarForNavigation()
    }

    const handleToggleQueueMode = () => {
        if (queueModeManager.isActive) {
            queueModeManager.stop()
            return
        }

        const firstTask = queueModeManager.start()
        if (!firstTask) return
        if (!queueModeManager.hasCapacity) {
            queueModeManager.waitForCapacity()
            return
        }
        navigate.go("CodeWorkspaceTask", { workspaceId: firstTask.workspaceId, taskId: firstTask.taskId })
        codeSidebarManager.closeSidebarForNavigation()
    }

    const handleShowOnlyRepo = (repoId: string) => {
        setHiddenRepoIds(
            new Set(
                hiddenWorkspaceIdsForOnly(
                    activeRepos.map((repo) => repo.id),
                    repoId
                )
            )
        )
    }

    const handleToggleRepoVisibility = (repoId: string) => {
        if (suppressSelectAfterDragRef.current) return
        setHiddenRepoIds((current) => {
            const next = new Set(current)
            if (next.has(repoId)) next.delete(repoId)
            else next.add(repoId)
            return next
        })
    }

    const handleToggleRemoteVisibility = (project: RemoteFleetProject) => {
        const current = new Set(excludedFor(project.nodeId))
        if (current.has(project.id)) current.delete(project.id)
        else current.add(project.id)
        setRemoteExcludedByNode((map) => new Map(map).set(project.nodeId, current))
        const record = loadWorkspaceSidebarState(project.nodeId)
        saveWorkspaceSidebarState(project.nodeId, {
            archivedVisible: record?.archivedVisible ?? false,
            hiddenWorkspaceIds: [...current],
            workspaceOrder: record?.workspaceOrder ?? [],
        })
    }

    const handleToggleHidden = (projectId: string) => {
        setHiddenProjectIds((current) => {
            const next = new Set(current)
            if (next.has(projectId)) next.delete(projectId)
            else next.add(projectId)
            return next
        })
        if (!hiddenProjectIds.has(projectId)) setShowHidden(true)
    }

    const handleOpenRemote = (project: RemoteFleetProject) => {
        if (!computers) return
        void computers.selectScope(project.nodeId)
        codeSidebarManager.closeSidebarForNavigation()
    }

    const handleSettingsRepo = (repoId: string) => {
        navigate.go("CodeWorkspaceSettings", { workspaceId: repoId })
        codeSidebarManager.closeSidebarForNavigation()
    }

    const handleNewTask = (repoId: string) => {
        navigate.openTaskCreate(repoId)
        codeSidebarManager.closeSidebarForNavigation()
    }

    const handleDeleteRepo = async (repoId: string) => {
        await codeStore.repos.removeRepo(repoId)
        setHiddenRepoIds((current) => {
            const next = new Set(current)
            next.delete(repoId)
            return next
        })
        if (workspaceId === repoId) {
            navigate.go("Code")
        }
    }

    const handleToggleArchive = async (repoId: string) => {
        const repo = allRepos.find((r) => r.id === repoId)
        if (!repo) return
        const wasArchived = !!repo.archived
        await codeStore.repos.setRepoArchived(repoId, !wasArchived)
        setHiddenRepoIds((current) => {
            const next = new Set(current)
            next.delete(repoId)
            return next
        })
        if (!wasArchived) {
            setShowArchived(true)
        }
        // If archiving the currently selected repo, navigate away
        if (!wasArchived && workspaceId === repoId) {
            navigate.go("Code")
        }
    }

    const renderRemoteItem = (project: RemoteFleetProject) => {
        const isHidden = hiddenProjectIds.has(project.id)
        return (
            <RepoItemRow
                key={`${project.nodeId}:${project.id}`}
                project={remoteToProject(project)}
                projectColor={activeWorkspaceColorById.get(project.id) ?? projectColorAssignments([project]).get(project.id) ?? "var(--color-muted)"}
                shown={!isHidden && !excludedFor(project.nodeId).has(project.id)}
                runningCount={remoteFleetTasks(computers?.overviews[project.nodeId], project.id).filter((task) => task.running).length}
                isArchived={false}
                isHidden={isHidden}
                isDragging={false}
                dropEdge={null}
                canMoveUp={false}
                canMoveDown={false}
                onSelect={() => {
                    if (isHidden) handleToggleHidden(project.id)
                    else handleToggleRemoteVisibility(project)
                }}
                showOnlyDisabled
                onDragStart={(event) => event.preventDefault()}
                onDragOver={() => undefined}
                onDrop={(event) => event.preventDefault()}
                onDragEnd={() => undefined}
                onNewTask={() => handleOpenRemote(project)}
                onMoveUp={() => undefined}
                onMoveDown={() => undefined}
                onSettings={() => handleOpenRemote(project)}
                onDelete={() => undefined}
                onToggleArchive={() => undefined}
                onToggleHidden={() => handleToggleHidden(project.id)}
                onOpen={() => handleOpenRemote(project)}
                onPreviewChange={setPreviewProjectName}
            />
        )
    }

    const renderRepoItem = (repo: Repo) => {
        const peers = repo.archived ? archivedRepos : activeRepos
        const peerIndex = peers.findIndex((peer) => peer.id === repo.id)
        const isHidden = !repo.archived && hiddenProjectIds.has(repo.id)
        return (
            <RepoItemRow
                key={repo.id}
                project={repoToProject(repo)}
                projectColor={
                    (repo.archived ? archivedWorkspaceColorById : activeWorkspaceColorById).get(repo.id) ??
                    projectColorAssignments([repo]).get(repo.id) ??
                    "var(--color-muted)"
                }
                shown={!repo.archived && !hiddenRepoIds.has(repo.id)}
                runningCount={getRunningCount(repo.id)}
                isArchived={!!repo.archived}
                isHidden={isHidden}
                isDragging={draggedRepoId === repo.id}
                dropEdge={workspaceDropTarget?.repoId === repo.id ? workspaceDropTarget.edge : null}
                canMoveUp={peerIndex > 0}
                canMoveDown={peerIndex >= 0 && peerIndex < peers.length - 1}
                onSelect={() => {
                    if (repo.archived) void handleToggleArchive(repo.id)
                    else if (isHidden) handleToggleHidden(repo.id)
                    else handleToggleRepoVisibility(repo.id)
                }}
                onShowOnly={repo.archived || activeRepos.length < 2 ? undefined : () => handleShowOnlyRepo(repo.id)}
                showOnlyDisabled={shownTaskRepos.length === 1 && shownTaskRepos[0]?.id === repo.id}
                onDragStart={(event) => handleWorkspaceDragStart(event, repo.id)}
                onDragOver={(event) => handleWorkspaceDragOver(event, repo)}
                onDrop={(event) => handleWorkspaceDrop(event, repo.id)}
                onDragEnd={resetWorkspaceDrag}
                onNewTask={() => handleNewTask(repo.id)}
                onMoveUp={() => moveWorkspaceByOffset(repo, -1)}
                onMoveDown={() => moveWorkspaceByOffset(repo, 1)}
                onSettings={() => handleSettingsRepo(repo.id)}
                onDelete={() => handleDeleteRepo(repo.id)}
                onToggleArchive={() => handleToggleArchive(repo.id)}
                onToggleHidden={() => handleToggleHidden(repo.id)}
                onShare={shareProject ? () => shareProject(repo.id) : undefined}
                onPreviewChange={setPreviewProjectName}
            />
        )
    }

    return (
        <div className="flex h-full min-h-0 flex-col">
            <div className="max-h-[36%] flex-none overflow-y-auto overscroll-contain border-b border-border pb-2">
                <div className="sticky top-0 z-10 flex min-h-8 items-center justify-between gap-2 bg-base-200 px-3">
                    <h2 className="flex-none text-xs font-medium text-muted">Projects</h2>
                    <span className="min-w-0 truncate text-right text-[11px] text-base-content/70" data-project-name-preview="true">
                        {projectNamePreview}
                    </span>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 px-3 pt-1.5 pb-1" data-project-grid="true">
                    {activeRepos.map((repo) => renderRepoItem(repo))}
                    {visibleRemote.map((project) => renderRemoteItem(project))}
                    <button
                        type="button"
                        className="btn flex h-8 min-w-8 items-center justify-center border border-dashed border-border/70 px-1.5 text-muted transition-[background-color,border-color,color] hover:border-primary/60 hover:bg-primary/10 hover:text-primary max-[900px]:h-11 max-[900px]:min-w-12"
                        onClick={handleAddRepo}
                        hidden={codeStore.isGuest}
                        title="Add project"
                        aria-label="Add project"
                    >
                        <Plus className="h-3.5 w-3.5" />
                    </button>
                    {archivedRepos.length > 0 && (
                        <>
                            <button
                                type="button"
                                className={cx(
                                    "btn flex h-8 flex-none items-center justify-center gap-1 px-1.5 text-[10px] text-muted transition-colors hover:bg-base-300 hover:text-base-content max-[900px]:h-11 max-[900px]:px-2",
                                    showArchived && "bg-base-300 text-base-content"
                                )}
                                onClick={() => setShowArchived((prev) => !prev)}
                                aria-label={`Archived projects (${archivedRepos.length})`}
                                aria-expanded={showArchived}
                            >
                                <Archive className="h-3 w-3 flex-none" />
                                <span className="tabular-nums">{archivedRepos.length}</span>
                                {showArchived ? <ChevronDown className="h-2.5 w-2.5" /> : <ChevronRight className="h-2.5 w-2.5" />}
                            </button>
                            {showArchived && archivedRepos.map((repo) => renderRepoItem(repo))}
                        </>
                    )}
                </div>
                {!codeStore.isGuest && <DiscoveryButton />}
                {allRepos.length === 0 && remoteProjects.length === 0 ? (
                    <div className="px-3 pt-1 text-[10px] text-muted">Add a directory on this computer to get started.</div>
                ) : (
                    <div className="px-1.5">
                        {hiddenRepos.length + hiddenRemote.length > 0 && (
                            <>
                                <button
                                    type="button"
                                    className="btn mt-1 flex cursor-pointer select-none items-center gap-1 px-3 py-1 text-xs text-muted transition-colors hover:text-base-content"
                                    onClick={() => setShowHidden((prev) => !prev)}
                                    aria-expanded={showHidden}
                                >
                                    {showHidden ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                    <span>Hidden ({hiddenRepos.length + hiddenRemote.length})</span>
                                </button>
                                <GridDisclosure
                                    open={showHidden}
                                    renderContent={() => (
                                        <div className="flex flex-wrap gap-1.5 px-1.5 pt-1.5 pb-1">
                                            {hiddenRepos.map((repo) => renderRepoItem(repo))}
                                            {hiddenRemote.map((project) => renderRemoteItem(project))}
                                        </div>
                                    )}
                                />
                            </>
                        )}
                    </div>
                )}
            </div>

            <div className="flex min-h-10 flex-none items-center justify-between gap-2 pl-3 pr-2">
                <div className="flex min-w-0 items-baseline gap-2">
                    <h2 className="text-xs font-medium text-muted">Tasks</h2>
                    <span className="truncate text-[10px] text-muted/70">
                        {shownTaskRepos.length + shownRemote.length === activeRepos.length + visibleRemote.length
                            ? `${activeRepos.length + visibleRemote.length} ${activeRepos.length + visibleRemote.length === 1 ? "project" : "projects"}`
                            : `${shownTaskRepos.length + shownRemote.length} of ${activeRepos.length + visibleRemote.length}`}
                    </span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="relative h-7 w-7 flex-none max-[900px]:h-10 max-[900px]:w-10" data-queue-mode-toggle-slot="true">
                        <button
                            type="button"
                            className={cx(
                                "group/q-toggle btn absolute right-0 top-0 z-10 h-7 w-7 overflow-hidden px-0 font-mono text-[9px] font-semibold tracking-[0.12em] transition-colors duration-200 motion-reduce:transition-none hover:w-[4.75rem] hover:ring-1 hover:ring-primary/40 max-[900px]:h-10 max-[900px]:w-10 max-[900px]:hover:w-10",
                                queueModeManager.isActive
                                    ? "bg-primary/10 text-primary hover:bg-primary/20"
                                    : "text-muted hover:bg-primary/10 hover:text-primary disabled:cursor-default disabled:opacity-40 disabled:hover:w-7 disabled:hover:bg-transparent disabled:hover:text-muted disabled:hover:ring-0 max-[900px]:disabled:hover:w-10"
                            )}
                            onClick={handleToggleQueueMode}
                            disabled={!queueModeManager.isActive && queueModeManager.eligibleTaskCount === 0}
                            aria-label={queueModeManager.isActive ? "Pause queue mode" : "Start queue mode"}
                            aria-pressed={queueModeManager.isActive}
                            title={
                                queueModeManager.isActive
                                    ? "Pause Q mode"
                                    : queueModeManager.eligibleTaskCount > 0
                                      ? `Start queue mode with ${queueModeManager.eligibleTaskCount} ready task${queueModeManager.eligibleTaskCount === 1 ? "" : "s"}`
                                      : "No unpinned idle tasks"
                            }
                        >
                            <span className="absolute inset-y-0 right-0 flex w-7 items-center justify-center max-[900px]:w-10">
                                {queueModeManager.isActive ? (
                                    <Pause className="h-3.5 w-3.5 flex-none" fill="currentColor" />
                                ) : (
                                    <Play className="h-3.5 w-3.5 flex-none" fill="currentColor" />
                                )}
                            </span>
                            <span className="absolute inset-y-0 left-2 flex items-center opacity-0 transition-opacity duration-200 motion-reduce:transition-none group-hover/q-toggle:opacity-100 max-[900px]:hidden">
                                {queueModeManager.isActive ? "PAUSE" : "PLAY"}
                            </span>
                        </button>
                    </div>
                </div>
            </div>

            <ScrollArea
                className="min-h-0 flex-1"
                viewportClassName="h-full"
                scrollbarClassName="pointer-events-none opacity-0 transition-[background-color,opacity] data-[scrolling]:pointer-events-auto data-[scrolling]:opacity-100"
            >
                <div className="pb-5">{renderMergedContent(shownTaskRepos, activeWorkspaceColorById, remoteQueues)}</div>
            </ScrollArea>
        </div>
    )
})
